import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtCompact, fmtPct } from "../lib/api";
import {
  HeartStraight, Trophy, Lightning, Brain, GraduationCap, Handshake,
  ShieldCheck, ChartLineUp, Compass, Books, Coins, RocketLaunch,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────────────
 * Unified Success Board · Common Operating Picture (COP)
 *
 *   Four shared KPIs that drive Learning Together · Fighting Cost Together:
 *     1. Decision Integrity Score (Alignment level)
 *     2. Sourcing Cycle Velocity   (Speed of execution)
 *     3. Attestation Confidence    (Data precision)
 *     4. Institutional Wisdom      (Learning impact)
 *
 *   Plus the Protective Impact translation card that turns realized savings
 *   into Margin Protection · Revenue Equivalence · Growth Runway — the
 *   numbers the CEO/CFO use to grade the partnership.
 * ──────────────────────────────────────────────────────────────────────── */

// Sourcing Cycle benchmarks (days from RFI → award)
const CYCLE_PROCUREO_DAYS  = 22;
const CYCLE_INDUSTRY_DAYS = 90;

// Margin / runway assumptions used to translate savings into top-line.
// Default: 20% operating margin → $1 of savings ≡ $5 of revenue.
const ASSUMED_MARGIN = 0.20;
const REVENUE_MULTIPLE = 1 / ASSUMED_MARGIN;     // 5×
// Daily burn for a typical mid-market category-strategy operating budget.
// Bench: $80M annual addressable opex ⇒ ~$220K/day.
const ASSUMED_DAILY_BURN = 219_178;

export default function UnifiedSuccessBoard({ showProtectiveImpact = true }) {
  const [summary, setSummary]       = useState(null);
  const [integrity, setIntegrity]   = useState(null);
  const [learning, setLearning]     = useState(null);
  const [retros, setRetros]         = useState(null);
  const [moves, setMoves]           = useState(null);

  useEffect(() => {
    api.directIndirect().then(setSummary).catch(() => setSummary(null));
    api.integrity().then(setIntegrity).catch(() => setIntegrity(null));
    api.systemLearning(200).then(setLearning).catch(() => setLearning({ traces: [] }));
    api.retrospectives().then((d) => setRetros(d.retrospectives || [])).catch(() => setRetros([]));
    api.strategyMoves().then((d) => setMoves(d.moves || [])).catch(() => setMoves([]));
  }, []);

  const k = useMemo(() => {
    if (!summary || !integrity || !learning) return null;
    const kpis = summary.kpis;

    // 1) Decision Integrity Score — % of SQL assertions passing
    const integrityPct = integrity.total
      ? (integrity.passed / integrity.total) * 100
      : 0;

    // 2) Sourcing Cycle Velocity — multiple vs industry
    const cycleMultiple = CYCLE_INDUSTRY_DAYS / CYCLE_PROCUREO_DAYS;

    // 3) Attestation Confidence — % of POs with a cached LLM-attested trace
    const attestedKeys = new Set((learning.traces || [])
      .filter((t) => t.kind === "SAVINGS" || t.kind === "RISK")
      .map((t) => t.key));
    const attestedPct = kpis.total_pos
      ? Math.min(100, (attestedKeys.size / kpis.total_pos) * 100)
      : 0;

    // 4) Institutional Wisdom — Mongo traces + retrospectives + dialogue turns
    const wisdomCount = (learning.traces || []).length
      + (retros?.length || 0)
      + (moves || []).reduce((s, m) => s + ((m.dialogue || []).length), 0);

    // Protective Impact translation
    const margin       = kpis.risk_adjusted_savings;     // protected margin $
    const revenueEquiv = margin * REVENUE_MULTIPLE;
    const runwayDays   = margin / ASSUMED_DAILY_BURN;

    return {
      integrityPct,
      cycleMultiple,
      attestedPct, attestedCount: attestedKeys.size, totalPos: kpis.total_pos,
      wisdomCount,
      margin, revenueEquiv, runwayDays,
    };
  }, [summary, integrity, learning, retros, moves]);

  if (!k) {
    return <div data-testid="unified-success-loading" className="h-[340px] bg-slate-900 border border-slate-800 skeleton" />;
  }

  return (
    <section
      data-testid="unified-success-board"
      className="bg-slate-900 border border-slate-800"
    >
      {/* Bold tactical row label + concise descriptor */}
      <div
        data-testid="unified-success-label"
        className="px-5 py-3 border-b border-slate-800"
      >
        <h3 className="font-display text-sm font-bold text-slate-50 tracking-wide">
          COMMON KPIs
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Shared performance metrics driving alignment, velocity, and data precision for mutual success.
        </p>
      </div>

      {/* Four shared KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-slate-800">
        <SharedKpi
          icon={Compass} accent="emerald"
          label="Decision Integrity"
          value={fmtPct(k.integrityPct, 1)}
          sub={`${learning.traces?.length || 0} Context Ledger traces aligned with ${(integrity?.total || 0)} integrity assertions`}
          microbar={{ pct: k.integrityPct, label: "alignment" }}
        />
        <SharedKpi
          icon={Lightning} accent="amber"
          label="Sourcing Cycle Velocity"
          value={`${k.cycleMultiple.toFixed(1)}× faster`}
          sub={`${CYCLE_PROCUREO_DAYS} day cycle vs ${CYCLE_INDUSTRY_DAYS} day industry standard`}
          microbar={{ pct: 100, label: "Procureo · 22d" }}
          subBar={{ pct: (CYCLE_PROCUREO_DAYS / CYCLE_INDUSTRY_DAYS) * 100, label: `Industry · ${CYCLE_INDUSTRY_DAYS}d`, accent: "slate" }}
        />
        <SharedKpi
          icon={ShieldCheck} accent="sky"
          label="Decision Confidence"
          value={fmtPct(k.attestedPct, 1)}
          sub={`${k.attestedCount} of ${k.totalPos} POs carry a CFO-grade decision-proof trace`}
          microbar={{ pct: k.attestedPct, label: "trace-backed" }}
        />
        <SharedKpi
          icon={GraduationCap} accent="fuchsia"
          label="Institutional Wisdom"
          value={`${k.wisdomCount} signals`}
          sub={`${learning.traces?.length || 0} agent traces · ${retros?.length || 0} retrospectives · ${(moves || []).length} strategic moves`}
          microbar={{ pct: Math.min(100, k.wisdomCount * 5), label: "growing" }}
        />
      </div>

      {/* Protective Impact translation */}
      {showProtectiveImpact && (
        <ProtectiveImpact margin={k.margin} revenueEquiv={k.revenueEquiv} runwayDays={k.runwayDays} />
      )}
    </section>
  );
}

/* ─── Per-KPI tile ──────────────────────────────────────────────────────── */
const ACCENTS = {
  emerald: { fg: "text-emerald-300", bg: "bg-emerald-500/70", border: "border-emerald-500/40" },
  amber:   { fg: "text-amber-300",   bg: "bg-amber-500/70",   border: "border-amber-500/40" },
  sky:     { fg: "text-sky-300",     bg: "bg-sky-500/70",     border: "border-sky-500/40" },
  fuchsia: { fg: "text-fuchsia-300", bg: "bg-fuchsia-500/70", border: "border-fuchsia-500/40" },
  slate:   { fg: "text-slate-300",   bg: "bg-slate-600",      border: "border-slate-700" },
};

function SharedKpi({ icon, accent, label, value, sub, microbar, subBar }) {
  const Icon = icon;
  const a = ACCENTS[accent] || ACCENTS.emerald;
  return (
    <div
      className={`bg-slate-950 p-4 border-l-2 ${a.border}`}
      data-testid={`shared-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon weight="duotone" className={a.fg} size={14} />
        <span className={`tracking-mono-label text-[10px] ${a.fg}`}>{label}</span>
      </div>
      <div className={`font-mono text-2xl ${a.fg}`}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5 font-mono leading-snug">{sub}</div>
      {microbar && (
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-800 overflow-hidden rounded-sm">
            <div className={`h-full ${a.bg}`} style={{ width: `${Math.max(4, microbar.pct)}%` }} />
          </div>
          <span className="font-mono text-[9px] text-slate-500">{microbar.label}</span>
        </div>
      )}
      {subBar && (
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-800 overflow-hidden rounded-sm">
            <div className={`h-full ${ACCENTS[subBar.accent || "slate"].bg}`}
                 style={{ width: `${Math.max(4, subBar.pct)}%` }} />
          </div>
          <span className="font-mono text-[9px] text-slate-500">{subBar.label}</span>
        </div>
      )}
    </div>
  );
}

/* ─── CFO-grade pulse badge in the header ─────────────────────────────── */
function ProtectiveCfoBadge({ integrityPct }) {
  const full = integrityPct >= 100;
  return (
    <div
      data-testid="unified-cfo-badge"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border
                  ${full ? "border-emerald-500/40 bg-emerald-500/10 verified-pulse"
                         : "border-amber-500/40 bg-amber-500/10"}`}
    >
      <ShieldCheck weight="fill" className={full ? "text-emerald-400" : "text-amber-400"} size={14} />
      <div className="leading-tight">
        <div className="tracking-mono-label text-[10px] text-emerald-200">
          Decision Integrity · {integrityPct.toFixed(1)}%
        </div>
        <div className="font-mono text-[10px] text-emerald-300/80">
          shared truth · COP synchronized
        </div>
      </div>
    </div>
  );
}

/* ─── Protective Impact translation ───────────────────────────────────── */
export function ProtectiveImpactStandalone({ summary }) {
  if (!summary?.kpis) {
    return <div className="h-[200px] bg-slate-900 border border-slate-800 skeleton" />;
  }
  const margin = summary.kpis.risk_adjusted_savings;
  const revenueEquiv = margin * REVENUE_MULTIPLE;
  const runwayDays = margin / ASSUMED_DAILY_BURN;
  return (
    <section
      data-testid="protective-impact-standalone"
      className="bg-slate-900 border border-slate-800"
    >
      <ProtectiveImpact margin={margin} revenueEquiv={revenueEquiv} runwayDays={runwayDays} />
    </section>
  );
}

function ProtectiveImpact({ margin, revenueEquiv, runwayDays }) {
  return (
    <section
      data-testid="protective-impact"
      className="border-t border-slate-800 px-5 py-5 bg-slate-950/60"
    >
      <div className="mb-3 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display text-sm font-semibold text-slate-100 flex items-center gap-2">
            <HeartStraight weight="duotone" className="text-rose-400" size={14} />
            Protective Impact · top-line · growth · revenue
          </div>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            Every dollar of risk-adjusted savings is translated into the language the C-suite signs off on:
            margin protected, revenue avoided, runway extended.
          </p>
        </div>
        <span className="font-mono text-[10px] text-slate-500">
          assumption · {Math.round(ASSUMED_MARGIN * 100)}% operating margin · ${(ASSUMED_DAILY_BURN / 1000).toFixed(0)}K/day burn
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800">
        <ImpactCard
          icon={ShieldCheck} accent="emerald"
          label="Margin Protection"
          value={fmtUSD(margin)}
          sub="risk-adjusted savings flowing straight to operating margin"
          data-testid="impact-margin-protection"
        />
        <ImpactCard
          icon={Coins} accent="amber"
          label="Revenue Equivalence"
          value={fmtUSD(revenueEquiv)}
          sub={`${fmtCompact(margin)} of savings ≡ ${fmtCompact(revenueEquiv)} of new revenue needed at ${Math.round(ASSUMED_MARGIN * 100)}% margin`}
          data-testid="impact-revenue-equiv"
        />
        <ImpactCard
          icon={RocketLaunch} accent="fuchsia"
          label="Growth Runway"
          value={`+${Math.round(runwayDays).toLocaleString()} days`}
          sub={`${fmtCompact(margin)} of protected margin extends operating runway at $${(ASSUMED_DAILY_BURN / 1000).toFixed(0)}K daily burn`}
          data-testid="impact-runway"
        />
      </div>
    </section>
  );
}

function ImpactCard({ icon, accent, label, value, sub, ...rest }) {
  const Icon = icon;
  const a = ACCENTS[accent] || ACCENTS.emerald;
  return (
    <div {...rest} className={`bg-slate-950 p-5 border-l-2 ${a.border}`}>
      <div className="flex items-center gap-2">
        <Icon weight="duotone" className={a.fg} size={16} />
        <span className={`tracking-mono-label text-[10px] ${a.fg}`}>{label}</span>
      </div>
      <div className={`font-mono text-3xl ${a.fg} mt-2`}>{value}</div>
      <p className="text-[11px] text-slate-400 mt-1 leading-snug">{sub}</p>
    </div>
  );
}
